package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ConditionalContentAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ForContentAst
import top.wcpe.allincore.dsl.compiler.ast.ModifierAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.source.SourcePosition
import top.wcpe.allincore.dsl.ir.material.MaterialGraphContract
import top.wcpe.allincore.dsl.ir.material.MaterialGraphContract.ValueType

class MaterialGraphValidationPass(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        checkElement(screen.root)
    }

    private fun checkElement(element: ElementAst) {
        element.modifiers.filter { it.kind == "materialGraph" }.forEach(::checkModifier)
        element.children.forEach(::checkElement)
        element.conditionalContent?.let(::checkConditional)
        element.forContent?.let(::checkFor)
    }

    private fun checkConditional(content: ConditionalContentAst) {
        content.branches.forEach { checkElement(it.body) }
        content.elseBranch?.let(::checkElement)
    }

    private fun checkFor(content: ForContentAst) {
        checkElement(content.body)
    }

    private fun checkModifier(modifier: ModifierAst) {
        val fields = modifier.fields.associateBy { it.name }
        val graph = stringLiteral(fields["graph"]?.value)
        if (graph == null) {
            diag.error(DslError.AUI_MG_STATIC_GRAPH_REQUIRED, modifier.position)
            return
        }
        checkFallback(fields["fallback"]?.value, modifier.position)
        val budget = parseBudget(fields["budget"]?.value, modifier.position) ?: return
        validateGraph(graph, budget, modifier.position)
    }

    private fun checkFallback(expr: ExprAst?, position: SourcePosition) {
        if (expr == null) return
        val fallback = stringLiteral(expr)
        if (fallback == null || !MaterialGraphContract.isFallbackKind(fallback)) {
            diag.error(
                DslError.AUI_MG_STATIC_FALLBACK_REQUIRED,
                position,
                MaterialGraphContract.fallbackKinds().joinToString(", "),
            )
        }
    }

    private fun parseBudget(expr: ExprAst?, position: SourcePosition): Int? {
        if (expr == null) return MaterialGraphContract.DEFAULT_BUDGET
        val number = numberLiteral(expr)?.toDouble()
        val validNumber = number?.takeIf {
            it.isFinite() &&
                it % 1.0 == 0.0 &&
                it >= 1.0 &&
                it <= MaterialGraphContract.MAX_BUDGET.toDouble()
        }
        if (validNumber == null) {
            diag.error(DslError.AUI_MG_INVALID_BUDGET, position, MaterialGraphContract.MAX_BUDGET)
            return null
        }
        return validNumber.toInt()
    }

    private fun validateGraph(source: String, budget: Int, position: SourcePosition) {
        val result = MaterialGraphParser(source).parse(position)
        result.errors.forEach { diag.errorWithArgs(it.error, it.position, it.args) }
        if (result.errors.isNotEmpty()) return
        MaterialGraphSemantic(result.nodes, budget, diag, position).check()
    }

    private fun stringLiteral(expr: ExprAst?): String? = (expr as? ExprAst.Literal.StringLit)?.value

    private fun numberLiteral(expr: ExprAst?): Number? = (expr as? ExprAst.Literal.NumberLit)?.value
}

private class MaterialGraphParser(private val source: String) {

    fun parse(position: SourcePosition): MaterialGraphParseResult {
        val nodes = LinkedHashMap<String, MaterialGraphNode>()
        val outputs = mutableListOf<String>()
        val errors = mutableListOf<MaterialGraphParseError>()
        splitStatements().forEach { statement -> parseStatement(statement, position, nodes, outputs, errors) }
        markOutputs(nodes, outputs, errors, position)
        if (outputs.isEmpty()) errors += MaterialGraphParseError(DslError.AUI_MG_STATIC_GRAPH_REQUIRED, position)
        return MaterialGraphParseResult(nodes.values.toList(), errors)
    }

    private fun parseStatement(
        statement: String,
        position: SourcePosition,
        nodes: LinkedHashMap<String, MaterialGraphNode>,
        outputs: MutableList<String>,
        errors: MutableList<MaterialGraphParseError>,
    ) {
        if (statement.startsWith("output ")) {
            outputs += statement.removePrefix("output ").trim()
            return
        }
        val parsed = NODE_PATTERN.matchEntire(statement)
        if (parsed == null) {
            errors += MaterialGraphParseError(DslError.AUI_MG_STATIC_GRAPH_REQUIRED, position)
            return
        }
        addNode(parsed, nodes, errors, position)
    }

    private fun addNode(
        parsed: MatchResult,
        nodes: LinkedHashMap<String, MaterialGraphNode>,
        errors: MutableList<MaterialGraphParseError>,
        position: SourcePosition,
    ) {
        val name = parsed.groupValues[1]
        val kind = parsed.groupValues[2]
        if (nodes.containsKey(name)) {
            errors += MaterialGraphParseError(DslError.AUI_MG_DUPLICATE_NODE, position, arrayOf(name))
            return
        }
        if (MaterialGraphContract.nodeSpec(kind) == null) {
            errors += MaterialGraphParseError(DslError.AUI_MG_ILLEGAL_NODE, position, arrayOf(kind))
            return
        }
        val args = parseArgs(name, parsed.groupValues[3], position, errors)
        nodes[name] = MaterialGraphNode(name, kind, args.dependencies)
    }

    private fun parseArgs(
        nodeName: String,
        args: String,
        position: SourcePosition,
        errors: MutableList<MaterialGraphParseError>,
    ): MaterialGraphArgs {
        val dependencies = mutableListOf<String>()
        args.split(',').map(String::trim).filter(String::isNotEmpty).forEach { part ->
            val keyValue = part.split('=', limit = 2)
            if (keyValue.size == 1 && part.matches(IDENT_PATTERN)) dependencies += part
            if (keyValue.size == 2 && !isStaticValue(keyValue[1].trim())) {
                errors += MaterialGraphParseError(
                    DslError.AUI_MG_DYNAMIC_EXPRESSION,
                    position,
                    arrayOf(nodeName, keyValue[0].trim()),
                )
            }
        }
        return MaterialGraphArgs(dependencies)
    }

    private fun markOutputs(
        nodes: LinkedHashMap<String, MaterialGraphNode>,
        outputs: List<String>,
        errors: MutableList<MaterialGraphParseError>,
        position: SourcePosition,
    ) {
        outputs.forEach { name ->
            val node = nodes[name]
            if (node == null) {
                errors += MaterialGraphParseError(DslError.AUI_MG_DANGLING_OUTPUT, position, arrayOf(name))
            } else {
                nodes[name] = node.copy(isOutput = true)
            }
        }
    }

    private fun splitStatements(): List<String> = source.split(';', '\n')
        .map(String::trim)
        .filter { it.isNotEmpty() && !it.startsWith("#") }

    private fun isStaticValue(value: String): Boolean {
        if (!value.matches(STATIC_VALUE_PATTERN)) return false
        if (value.toDoubleOrNull() != null) return true
        if (':' in value || '/' in value) return true
        return '.' !in value
    }

    private companion object {
        val NODE_PATTERN = Regex("([A-Za-z_][A-Za-z0-9_]*)\\s*=\\s*([A-Za-z_][A-Za-z0-9_]*)\\s*\\((.*)\\)")
        val IDENT_PATTERN = Regex("[A-Za-z_][A-Za-z0-9_]*")
        val STATIC_VALUE_PATTERN = Regex("[A-Za-z0-9_:#./+\\-]+")
    }
}

private class MaterialGraphSemantic(
    private val nodes: List<MaterialGraphNode>,
    private val budget: Int,
    private val diag: DiagnosticCollector,
    private val position: SourcePosition,
) {
    private val byName = nodes.associateBy { it.name }
    private val types = mutableMapOf<String, ValueType>()

    fun check() {
        checkBudget()
        checkUnknownDependencies()
        nodes.forEach { visit(it.name, LinkedHashSet()) }
        nodes.filter { it.isOutput }.forEach(::checkOutput)
    }

    private fun checkBudget() {
        val cost = nodes.sumOf { MaterialGraphContract.costOf(it.kind) }
        val edges = nodes.sumOf { it.dependencies.size }
        if (cost > budget || nodes.size > MaterialGraphContract.MAX_NODES || edges > MaterialGraphContract.MAX_EDGES) {
            diag.error(DslError.AUI_MG_OVER_BUDGET, position, cost, budget, nodes.size, edges)
        }
    }

    private fun checkUnknownDependencies() {
        nodes.forEach { node ->
            node.dependencies.filterNot(byName::containsKey).forEach { dependency ->
                diag.error(DslError.AUI_MG_UNKNOWN_DEPENDENCY, position, node.name, dependency)
            }
        }
    }

    private fun visit(name: String, stack: LinkedHashSet<String>): ValueType? {
        types[name]?.let { return it }
        val node = byName[name] ?: return null
        if (!stack.add(name)) {
            diag.error(DslError.AUI_MG_CYCLE, position, (stack + name).joinToString(" -> "))
            return null
        }
        val inputTypes = node.dependencies.mapNotNull { visit(it, stack) }
        stack.remove(name)
        return inferType(node, inputTypes)?.also { types[name] = it }
    }

    private fun inferType(node: MaterialGraphNode, inputTypes: List<ValueType>): ValueType? {
        // 解析阶段已拒绝未知 kind，此处 null 仅作防御。
        val spec = MaterialGraphContract.nodeSpec(node.kind) ?: return null
        if (node.dependencies.any { it !in byName } || !spec.accepts(inputTypes)) {
            if (node.dependencies.all { it in byName }) {
                diag.error(DslError.AUI_MG_TYPE_MISMATCH, position, node.name, node.kind)
            }
            return null
        }
        return spec.output()
    }

    private fun checkOutput(node: MaterialGraphNode) {
        if (types[node.name] != ValueType.MATERIAL) {
            diag.error(DslError.AUI_MG_TYPE_MISMATCH, position, node.name, node.kind)
        }
    }
}

private data class MaterialGraphParseResult(
    val nodes: List<MaterialGraphNode>,
    val errors: List<MaterialGraphParseError>,
)

private data class MaterialGraphParseError(
    val error: DslError,
    val position: SourcePosition,
    val args: Array<out Any?> = emptyArray(),
)

private data class MaterialGraphNode(
    val name: String,
    val kind: String,
    val dependencies: List<String>,
    val isOutput: Boolean = false,
)

private data class MaterialGraphArgs(val dependencies: List<String>)
